import { zodResolver } from '@hookform/resolvers/zod';
import {
  useIntegrationMqttGetAllBrokers,
  useGetMqttDevicePropertyDefinitions,
  MqttCreateDeviceRequestDeviceType,
  type DevicesDeviceType,
  type MqttBroker,
  type MqttBufferedMessage,
} from '@smarthome/api';
import { AlertCircle, Check, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  useForm,
  useFieldArray,
  useWatch,
  type Control,
  type FieldValues,
  type UseFormRegister,
} from 'react-hook-form';
import { z } from 'zod';

import { TextField, cn } from '@/components/ui';
import { getDeviceTypeLabel } from '@/features/devices';

// Property mapping schema
const propertyMappingSchema = z.object({
  propertyName: z.string().min(1),
  commandEnabled: z.boolean(),
  commandMode: z.enum(['simple', 'advanced']),
  commandTopicOverride: z.string().optional(),
  commandJsonPath: z.string().optional(),
  commandValueMap: z.record(z.string(), z.any()).optional(),
  commandTemplate: z.string().optional(),
  stateEnabled: z.boolean(),
  stateMode: z.enum(['simple', 'advanced']),
  stateTopicOverride: z.string().optional(),
  stateJsonPath: z.string().optional(),
  stateValueMap: z.record(z.string(), z.any()).optional(),
  stateQuery: z.string().optional(),
});

const switchPositionSchema = z.object({
  id: z.string().min(1, 'Position ID is required').max(50),
  label: z.string().min(1, 'Label is required').max(100),
  commandPayload: z.string().min(1, 'Command payload is required'),
  stateValue: z.string().min(1, 'State value is required'),
});

const switchPositionsSchema = z.object({
  positions: z.array(switchPositionSchema).min(2, 'At least 2 positions required'),
  stateJsonPath: z.string().min(1, 'State JSON path is required'),
});

const commandMappingSchema = z.object({
  command: z.string().min(1),
  commandEnabled: z.boolean(),
  commandMode: z.enum(['simple', 'advanced']),
  commandTopicOverride: z.string().optional(),
  commandJsonPath: z.string().optional(),
  commandValueMap: z.record(z.string(), z.any()).optional(),
  commandTemplate: z.string().optional(),
});

// Device form schema
const deviceFormSchema = z
  .object({
    id: z.string().min(1, 'ID is required').max(255),
    brokerId: z.string().min(1, 'Broker is required'),
    name: z.string().min(1, 'Name is required').max(255),
    deviceType: z.enum(['switch', 'light', 'window_covering', 'fan', 'speaker', 'tv'], {
      required_error: 'Device type is required',
    }),
    commandTopic: z.string().min(1, 'Command topic is required').max(500),
    stateTopic: z.string().max(500).optional(),
    switchPositions: switchPositionsSchema.optional(),
    propertyMappings: z.array(propertyMappingSchema),
    commandMappings: z.array(commandMappingSchema),
  })
  .superRefine((data, ctx) => {
    // If it's a switch with positions, property mappings are optional
    const hasPositions =
      data.switchPositions?.positions && data.switchPositions.positions.length >= 2;
    if (!hasPositions && data.propertyMappings.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 1,
        type: 'array',
        inclusive: true,
        message: 'At least one property mapping required',
        path: ['propertyMappings'],
      });
    }
  });

export type DeviceFormData = z.infer<typeof deviceFormSchema>;

export interface MqttDeviceFormProps {
  /** Form ID for external submit buttons */
  formId: string;
  /** Initial values for editing, or partial values for creating */
  initialValues?: Partial<DeviceFormData>;
  /** Pre-selected broker (when creating from monitor page) */
  preselectedBrokerId?: string;
  /** Sample message for context (when creating from monitor page) */
  sampleMessage?: MqttBufferedMessage | null;
  /** Called when form is submitted */
  onSubmit: (data: DeviceFormData) => Promise<void>;
  /** Whether the form is in loading state */
  isLoading?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Whether this is editing an existing device (hides ID and device type) */
  isEditing?: boolean;
  /** Optional list of brokers to use instead of fetching */
  brokers?: MqttBroker[];
}

export function MqttDeviceForm({
  formId,
  initialValues,
  preselectedBrokerId,
  sampleMessage,
  onSubmit,
  isLoading: _isLoading,
  error,
  isEditing,
  brokers: providedBrokers,
}: MqttDeviceFormProps) {
  const { data: brokersData } = useIntegrationMqttGetAllBrokers({
    query: { enabled: !providedBrokers },
  });
  const brokers = providedBrokers ?? brokersData?.brokers ?? [];
  const [expandedProperty, setExpandedProperty] = useState<string | null>(null);
  const [expandedCommand, setExpandedCommand] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<DeviceFormData>({
    resolver: zodResolver(deviceFormSchema),
    defaultValues: {
      id: initialValues?.id ?? '',
      brokerId: initialValues?.brokerId ?? preselectedBrokerId ?? '',
      name: initialValues?.name ?? sampleMessage?.topic?.split('/').pop() ?? '',
      deviceType: initialValues?.deviceType,
      commandTopic: initialValues?.commandTopic ?? sampleMessage?.topic ?? '',
      stateTopic: initialValues?.stateTopic ?? '',
      switchPositions: initialValues?.switchPositions ?? undefined,
      propertyMappings: initialValues?.propertyMappings ?? [],
      commandMappings: initialValues?.commandMappings ?? [],
    },
  });

  const { fields, replace } = useFieldArray({
    control,
    name: 'propertyMappings',
  });
  const { fields: commandFields, replace: replaceCommandMappings } = useFieldArray({
    control,
    name: 'commandMappings',
  });
  const {
    fields: positionFields,
    append: appendPosition,
    remove: removePosition,
  } = useFieldArray({
    control,
    name: 'switchPositions.positions',
  });
  const [expandedPosition, setExpandedPosition] = useState<number | null>(null);

  const deviceType = useWatch({ control, name: 'deviceType' });
  const switchPositions = useWatch({ control, name: 'switchPositions' });
  const hasPositions = switchPositions?.positions && switchPositions.positions.length >= 2;
  const isSwitch = deviceType === 'switch';

  // Fetch property definitions when device type changes
  const { data: propertyDefsData } = useGetMqttDevicePropertyDefinitions(
    { deviceType: deviceType ?? '' },
    { query: { enabled: !!deviceType } }
  );

  // Auto-populate property mappings when device type changes (only for new devices)
  useEffect(() => {
    if (!deviceType || isEditing) return;

    if (propertyDefsData?.properties) {
      const existingMappings = fields;
      // Only populate if we don't have mappings yet
      if (existingMappings.length === 0 || !initialValues?.propertyMappings) {
        const newMappings = propertyDefsData.properties.map((prop) => ({
          propertyName: prop.name ?? '',
          commandEnabled: true,
          commandMode: 'simple' as const,
          commandTopicOverride: '',
          commandJsonPath: prop.name ?? '',
          commandValueMap: {},
          commandTemplate: '',
          stateEnabled: true,
          stateMode: 'simple' as const,
          stateTopicOverride: '',
          stateJsonPath: prop.name ?? '',
          stateValueMap: {},
          stateQuery: '',
        }));
        replace(newMappings);
        setExpandedProperty(null);
      }
    }

    if (propertyDefsData?.commands) {
      const existingCommandMappings = commandFields;
      if (existingCommandMappings.length === 0 || !initialValues?.commandMappings) {
        const newCommandMappings = propertyDefsData.commands.map((command) => ({
          command: command.name ?? '',
          commandEnabled: true,
          commandMode: 'simple' as const,
          commandTopicOverride: '',
          commandJsonPath: '',
          commandValueMap: {},
          commandTemplate: '',
        }));
        replaceCommandMappings(newCommandMappings);
        setExpandedCommand(null);
      }
    }
  }, [
    propertyDefsData,
    deviceType,
    isEditing,
    replace,
    replaceCommandMappings,
    fields,
    commandFields,
    initialValues?.propertyMappings,
    initialValues?.commandMappings,
  ]);

  const getPropertyLabel = (name: string) => {
    const def = propertyDefsData?.properties?.find((p) => p.name === name);
    return def?.label ?? name;
  };

  const getCommandLabel = (name: string) => {
    const def = propertyDefsData?.commands?.find((command) => command.name === name);
    return def?.label ?? name;
  };

  const commandDefinitions = propertyDefsData?.commands ?? [];
  const hasCommandDefinitions = commandDefinitions.length > 0;

  const handleAddCommandMappings = () => {
    if (!hasCommandDefinitions) return;
    const mappings = commandDefinitions.map((command) => ({
      command: command.name ?? '',
      commandEnabled: true,
      commandMode: 'simple' as const,
      commandTopicOverride: '',
      commandJsonPath: '',
      commandValueMap: {},
      commandTemplate: '',
    }));
    replaceCommandMappings(mappings);
    setExpandedCommand(null);
  };

  const deviceTypeOptions = useMemo(
    () =>
      Object.values(MqttCreateDeviceRequestDeviceType).map((type) => ({
        value: type,
        label: getDeviceTypeLabel(type as DevicesDeviceType),
      })),
    []
  );

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Topic display (when creating from message) */}
      {sampleMessage && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-white/[0.06] dark:bg-zinc-900">
          <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
            Command Topic
          </div>
          <div className="mt-1 font-mono text-sm text-gray-900 dark:text-white">
            {sampleMessage.topic}
          </div>
        </div>
      )}

      {/* ID and Device Type row */}
      <div className="grid grid-cols-2 gap-4">
        {!isEditing && (
          <TextField
            label="Device ID"
            placeholder="living-room-light"
            error={errors.id?.message}
            {...register('id')}
          />
        )}
        {!isEditing ? (
          <Controller
            name="deviceType"
            control={control}
            render={({ field: { value, onChange } }) => (
              <DropdownSelect
                label="Device Type"
                value={value ?? null}
                onChange={(v) => onChange(v ?? '')}
                options={deviceTypeOptions}
                placeholder="Select type"
                error={errors.deviceType?.message}
              />
            )}
          />
        ) : (
          <div className="col-span-2" />
        )}
      </div>

      {/* Name and Broker row */}
      <div className="grid grid-cols-2 gap-4">
        <TextField
          label="Name"
          placeholder="Living Room Light"
          error={errors.name?.message}
          {...register('name')}
        />
        {!preselectedBrokerId && (
          <Controller
            name="brokerId"
            control={control}
            render={({ field: { value, onChange } }) => (
              <BrokerSelect
                label="Broker"
                value={value}
                onChange={onChange}
                brokers={brokers}
                error={errors.brokerId?.message}
              />
            )}
          />
        )}
      </div>

      {/* Topics row (when not from message) */}
      {!sampleMessage && (
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="Command Topic"
            placeholder="home/livingroom/light/set"
            error={errors.commandTopic?.message}
            {...register('commandTopic')}
          />
          <TextField
            label="State Topic (optional)"
            placeholder="home/livingroom/light/state"
            error={errors.stateTopic?.message}
            {...register('stateTopic')}
          />
        </div>
      )}

      {/* State topic when from message */}
      {sampleMessage && (
        <TextField
          label="State Topic (optional)"
          placeholder="home/livingroom/light/state"
          error={errors.stateTopic?.message}
          {...register('stateTopic')}
        />
      )}

      {/* Switch Positions Section */}
      {isSwitch && !isEditing && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
            Switch Positions
          </label>
          {positionFields.length === 0 ? (
            <div className="rounded-lg border border-gray-200 p-3 dark:border-white/[0.08]">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-gray-500 dark:text-zinc-500">
                  Configure positions for a multi-position switch (e.g. Low / Medium / High). Leave
                  empty for a simple on/off switch.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    appendPosition({ id: '', label: '', commandPayload: '', stateValue: '' });
                    appendPosition({ id: '', label: '', commandPayload: '', stateValue: '' });
                    setExpandedPosition(0);
                  }}
                  className="rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-white/[0.08] dark:text-zinc-200 dark:hover:bg-white/[0.04]"
                >
                  Add positions
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-white/[0.08]">
              {positionFields.map((field, index) => (
                <SwitchPositionCard
                  key={field.id}
                  index={index}
                  isExpanded={expandedPosition === index}
                  onToggle={() => setExpandedPosition(expandedPosition === index ? null : index)}
                  onRemove={positionFields.length > 2 ? () => removePosition(index) : undefined}
                  register={register}
                  errors={errors}
                />
              ))}
              <div className="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={() => {
                    appendPosition({ id: '', label: '', commandPayload: '', stateValue: '' });
                    setExpandedPosition(positionFields.length);
                  }}
                  className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add position
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Remove all positions and clear switchPositions
                    while (positionFields.length > 0) {
                      removePosition(0);
                    }
                  }}
                  className="text-sm text-gray-500 hover:text-red-600 dark:text-zinc-500 dark:hover:text-red-400"
                >
                  Remove positions
                </button>
              </div>
              <TextField
                label="State JSON Path"
                placeholder="Actual Mode"
                error={errors.switchPositions?.stateJsonPath?.message}
                {...register('switchPositions.stateJsonPath')}
              />
              {errors.switchPositions?.positions?.message && (
                <p className="text-sm text-red-600 dark:text-red-400">
                  {errors.switchPositions.positions.message}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Switch Positions Section (editing mode) */}
      {isSwitch && isEditing && positionFields.length > 0 && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
            Switch Positions
          </label>
          <div className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-white/[0.08]">
            {positionFields.map((field, index) => (
              <SwitchPositionCard
                key={field.id}
                index={index}
                isExpanded={expandedPosition === index}
                onToggle={() => setExpandedPosition(expandedPosition === index ? null : index)}
                onRemove={positionFields.length > 2 ? () => removePosition(index) : undefined}
                register={register}
                errors={errors}
              />
            ))}
            <div className="flex items-center justify-between pt-1">
              <button
                type="button"
                onClick={() => {
                  appendPosition({ id: '', label: '', commandPayload: '', stateValue: '' });
                  setExpandedPosition(positionFields.length);
                }}
                className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                <Plus className="h-3.5 w-3.5" />
                Add position
              </button>
            </div>
            <TextField
              label="State JSON Path"
              placeholder="Actual Mode"
              error={errors.switchPositions?.stateJsonPath?.message}
              {...register('switchPositions.stateJsonPath')}
            />
          </div>
        </div>
      )}

      {/* Property Mappings Section (hidden when multi-position switch) */}
      {fields.length > 0 && !hasPositions && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
            Property Mappings
          </label>
          <div className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-white/[0.08]">
            {fields.map((field, index) => (
              <PropertyMappingCard
                key={field.id}
                index={index}
                propertyName={field.propertyName}
                propertyLabel={getPropertyLabel(field.propertyName)}
                isExpanded={expandedProperty === field.propertyName}
                onToggle={() =>
                  setExpandedProperty(
                    expandedProperty === field.propertyName ? null : field.propertyName
                  )
                }
                control={control as unknown as Control<FieldValues>}
                register={register as unknown as UseFormRegister<FieldValues>}
              />
            ))}
          </div>
          {errors.propertyMappings?.message && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {errors.propertyMappings.message}
            </p>
          )}
        </div>
      )}

      {/* Command Mappings Section */}
      {(commandFields.length > 0 || hasCommandDefinitions) && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
            Command Mappings
          </label>
          <div className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-white/[0.08]">
            {commandFields.length === 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-gray-500 dark:text-zinc-500">
                  No command mappings configured.
                </p>
                <button
                  type="button"
                  onClick={handleAddCommandMappings}
                  className="rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-white/[0.08] dark:text-zinc-200 dark:hover:bg-white/[0.04]"
                >
                  Add command mappings
                </button>
              </div>
            ) : (
              commandFields.map((field, index) => (
                <CommandMappingCard
                  key={field.id}
                  index={index}
                  commandName={field.command}
                  commandLabel={getCommandLabel(field.command)}
                  isExpanded={expandedCommand === field.command}
                  onToggle={() =>
                    setExpandedCommand(expandedCommand === field.command ? null : field.command)
                  }
                  control={control as unknown as Control<FieldValues>}
                  register={register as unknown as UseFormRegister<FieldValues>}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Device type hint when no properties yet */}
      {!deviceType && !isEditing && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-center text-sm text-gray-500 dark:border-white/[0.06] dark:bg-zinc-900 dark:text-zinc-500">
          Select a device type to configure property mappings
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
          {error}
        </div>
      )}
    </form>
  );
}

// Property Mapping Card Component
interface PropertyMappingCardProps {
  index: number;
  propertyName: string;
  propertyLabel: string;
  isExpanded: boolean;
  onToggle: () => void;
  control: Control<FieldValues>;
  register: UseFormRegister<FieldValues>;
}

function PropertyMappingCard({
  index,
  propertyName,
  propertyLabel,
  isExpanded,
  onToggle,
  control,
  register,
}: PropertyMappingCardProps) {
  const commandEnabled = useWatch({
    control,
    name: `propertyMappings.${index}.commandEnabled`,
  });
  const stateEnabled = useWatch({
    control,
    name: `propertyMappings.${index}.stateEnabled`,
  });
  const commandMode = useWatch({
    control,
    name: `propertyMappings.${index}.commandMode`,
  });
  const stateMode = useWatch({
    control,
    name: `propertyMappings.${index}.stateMode`,
  });

  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 dark:border-white/[0.04] dark:bg-[#111113]">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between p-3"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-400" />
          )}
          <span className="text-sm font-medium text-gray-900 dark:text-white">{propertyLabel}</span>
          <span className="text-xs text-gray-500 dark:text-zinc-500">({propertyName})</span>
        </div>
        <div className="flex items-center gap-2">
          {commandEnabled && (
            <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700 dark:bg-blue-500/20 dark:text-blue-400">
              cmd
            </span>
          )}
          {stateEnabled && (
            <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-700 dark:bg-green-500/20 dark:text-green-400">
              state
            </span>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="space-y-4 border-t border-gray-200 p-3 dark:border-white/[0.06]">
          {/* Command Configuration */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-zinc-300">Command</span>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-zinc-700 dark:bg-zinc-900"
                  {...register(`propertyMappings.${index}.commandEnabled`)}
                />
                <span className="text-sm text-gray-500 dark:text-zinc-500">Enabled</span>
              </label>
            </div>
            {commandEnabled && (
              <div className="space-y-2 pl-4">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="simple"
                      className="h-4 w-4 border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-zinc-700"
                      {...register(`propertyMappings.${index}.commandMode`)}
                    />
                    <span className="text-sm text-gray-700 dark:text-zinc-300">Simple</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="advanced"
                      className="h-4 w-4 border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-zinc-700"
                      {...register(`propertyMappings.${index}.commandMode`)}
                    />
                    <span className="text-sm text-gray-700 dark:text-zinc-300">Advanced</span>
                  </label>
                </div>
                {commandMode === 'simple' ? (
                  <TextField
                    label="JSON Path"
                    placeholder="power"
                    {...register(`propertyMappings.${index}.commandJsonPath`)}
                  />
                ) : (
                  <TextField
                    label="JSONata Template"
                    placeholder='$boolean(value) ? "ON" : "OFF"'
                    {...register(`propertyMappings.${index}.commandTemplate`)}
                  />
                )}
                <TextField
                  label="Topic Override (optional)"
                  placeholder="Leave empty to use device topic"
                  {...register(`propertyMappings.${index}.commandTopicOverride`)}
                />
              </div>
            )}
          </div>

          {/* State Configuration */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-zinc-300">State</span>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-zinc-700 dark:bg-zinc-900"
                  {...register(`propertyMappings.${index}.stateEnabled`)}
                />
                <span className="text-sm text-gray-500 dark:text-zinc-500">Enabled</span>
              </label>
            </div>
            {stateEnabled && (
              <div className="space-y-2 pl-4">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="simple"
                      className="h-4 w-4 border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-zinc-700"
                      {...register(`propertyMappings.${index}.stateMode`)}
                    />
                    <span className="text-sm text-gray-700 dark:text-zinc-300">Simple</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="advanced"
                      className="h-4 w-4 border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-zinc-700"
                      {...register(`propertyMappings.${index}.stateMode`)}
                    />
                    <span className="text-sm text-gray-700 dark:text-zinc-300">Advanced</span>
                  </label>
                </div>
                {stateMode === 'simple' ? (
                  <TextField
                    label="JSON Path"
                    placeholder="power"
                    {...register(`propertyMappings.${index}.stateJsonPath`)}
                  />
                ) : (
                  <TextField
                    label="JSONata Query"
                    placeholder="state.power"
                    {...register(`propertyMappings.${index}.stateQuery`)}
                  />
                )}
                <TextField
                  label="Topic Override (optional)"
                  placeholder="Leave empty to use device topic"
                  {...register(`propertyMappings.${index}.stateTopicOverride`)}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Switch Position Card Component
interface SwitchPositionCardProps {
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onRemove?: () => void;
  register: UseFormRegister<DeviceFormData>;
  errors: ReturnType<typeof useForm<DeviceFormData>>['formState']['errors'];
}

function SwitchPositionCard({
  index,
  isExpanded,
  onToggle,
  onRemove,
  register,
  errors,
}: SwitchPositionCardProps) {
  const posErrors = errors.switchPositions?.positions?.[index];

  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 dark:border-white/[0.04] dark:bg-[#111113]">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between p-3"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-400" />
          )}
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            Position {index + 1}
          </span>
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-red-600 dark:hover:bg-white/[0.06] dark:hover:text-red-400"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </button>

      {isExpanded && (
        <div className="space-y-3 border-t border-gray-200 p-3 dark:border-white/[0.06]">
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Position ID"
              placeholder="low"
              error={posErrors?.id?.message}
              {...register(`switchPositions.positions.${index}.id`)}
            />
            <TextField
              label="Label"
              placeholder="Low"
              error={posErrors?.label?.message}
              {...register(`switchPositions.positions.${index}.label`)}
            />
          </div>
          <TextField
            label="Command Payload"
            placeholder='{"command": "low"}'
            error={posErrors?.commandPayload?.message}
            {...register(`switchPositions.positions.${index}.commandPayload`)}
          />
          <TextField
            label="State Match Value"
            placeholder="1"
            hint="Exact value or expression, e.g. value < 1.5"
            error={posErrors?.stateValue?.message}
            {...register(`switchPositions.positions.${index}.stateValue`)}
          />
        </div>
      )}
    </div>
  );
}

interface CommandMappingCardProps {
  index: number;
  commandName: string;
  commandLabel: string;
  isExpanded: boolean;
  onToggle: () => void;
  control: Control<FieldValues>;
  register: UseFormRegister<FieldValues>;
}

function CommandMappingCard({
  index,
  commandName,
  commandLabel,
  isExpanded,
  onToggle,
  control,
  register,
}: CommandMappingCardProps) {
  const commandEnabled = useWatch({
    control,
    name: `commandMappings.${index}.commandEnabled`,
  });
  const commandMode = useWatch({
    control,
    name: `commandMappings.${index}.commandMode`,
  });

  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 dark:border-white/[0.04] dark:bg-[#111113]">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between p-3"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-400" />
          )}
          <span className="text-sm font-medium text-gray-900 dark:text-white">{commandLabel}</span>
          <span className="text-xs text-gray-500 dark:text-zinc-500">({commandName})</span>
        </div>
        <div className="flex items-center gap-2">
          {commandEnabled && (
            <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700 dark:bg-blue-500/20 dark:text-blue-400">
              cmd
            </span>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="space-y-4 border-t border-gray-200 p-3 dark:border-white/[0.06]">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-zinc-300">Command</span>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-zinc-700 dark:bg-zinc-900"
                  {...register(`commandMappings.${index}.commandEnabled`)}
                />
                <span className="text-sm text-gray-500 dark:text-zinc-500">Enabled</span>
              </label>
            </div>
            {commandEnabled && (
              <div className="space-y-2 pl-4">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="simple"
                      className="h-4 w-4 border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-zinc-700"
                      {...register(`commandMappings.${index}.commandMode`)}
                    />
                    <span className="text-sm text-gray-700 dark:text-zinc-300">Simple</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="advanced"
                      className="h-4 w-4 border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-zinc-700"
                      {...register(`commandMappings.${index}.commandMode`)}
                    />
                    <span className="text-sm text-gray-700 dark:text-zinc-300">Advanced</span>
                  </label>
                </div>
                {commandMode === 'simple' ? (
                  <TextField
                    label="JSON Path"
                    placeholder="command"
                    {...register(`commandMappings.${index}.commandJsonPath`)}
                  />
                ) : (
                  <TextField
                    label="JSONata Template"
                    placeholder='"STOP"'
                    {...register(`commandMappings.${index}.commandTemplate`)}
                  />
                )}
                <TextField
                  label="Topic Override (optional)"
                  placeholder="Leave empty to use device topic"
                  {...register(`commandMappings.${index}.commandTopicOverride`)}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Broker dropdown component
interface BrokerSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  brokers: MqttBroker[];
  error?: string;
}

function BrokerSelect({ label, value, onChange, brokers, error }: BrokerSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedBroker = brokers.find((b) => b.id === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-all',
          'bg-white dark:bg-[#111113]',
          error
            ? 'border-red-500 focus:ring-red-500'
            : 'border-gray-200 hover:border-gray-300 dark:border-white/[0.08] dark:hover:border-white/[0.12]',
          isOpen && !error && 'border-blue-500 ring-1 ring-blue-500'
        )}
      >
        <span
          className={
            selectedBroker ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-zinc-500'
          }
        >
          {selectedBroker?.name ?? 'Select broker...'}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-gray-400 transition-transform dark:text-zinc-500',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1.5 w-full rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-white/[0.08] dark:bg-[#1c1c1f]">
          <div className="max-h-56 overflow-y-auto">
            {brokers.map((broker) => (
              <button
                key={broker.id}
                type="button"
                onClick={() => {
                  onChange(broker.id ?? '');
                  setIsOpen(false);
                }}
                className={cn(
                  'flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors',
                  'hover:bg-gray-50 dark:hover:bg-white/[0.04]',
                  value === broker.id && 'bg-blue-50 dark:bg-blue-500/10'
                )}
              >
                <div>
                  <div
                    className={cn(
                      'text-gray-900 dark:text-white',
                      value === broker.id && 'text-blue-600 dark:text-blue-400'
                    )}
                  >
                    {broker.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-zinc-500">
                    {broker.host}:{broker.port}
                  </div>
                </div>
                {value === broker.id && (
                  <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}

// Dropdown select for device type
interface DropdownSelectProps {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  error?: string;
}

function DropdownSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select...',
  error,
}: DropdownSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-all',
          'bg-white dark:bg-[#111113]',
          error
            ? 'border-red-500 focus:ring-red-500'
            : 'border-gray-200 hover:border-gray-300 dark:border-white/[0.08] dark:hover:border-white/[0.12]',
          isOpen && !error && 'border-blue-500 ring-1 ring-blue-500'
        )}
      >
        <span
          className={
            selectedOption ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-zinc-500'
          }
        >
          {selectedOption?.label ?? placeholder}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-gray-400 transition-transform dark:text-zinc-500',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1.5 w-full rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-white/[0.08] dark:bg-[#1c1c1f]">
          <div className="max-h-56 overflow-y-auto">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value || null);
                  setIsOpen(false);
                }}
                className={cn(
                  'flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors',
                  'hover:bg-gray-50 dark:hover:bg-white/[0.04]',
                  value === option.value && 'bg-blue-50 dark:bg-blue-500/10'
                )}
              >
                <span
                  className={cn(
                    'text-gray-900 dark:text-white',
                    value === option.value && 'text-blue-600 dark:text-blue-400'
                  )}
                >
                  {option.label}
                </span>
                {value === option.value && (
                  <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}
